import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { supabase } from "../supabaseClient";
import { isPrerendering } from "../utils/isPrerendering";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    /*
      react-snap opens each public route during npm run build.

      Do not call Supabase while prerendering because:
      - third-party requests may be blocked
      - session restoration is unnecessary
      - failed requests can produce react-snap page errors
    */
    if (isPrerendering()) {
      setSession(null);
      setUser(null);
      setInitialized(true);
      return undefined;
    }

    let mounted = true;

    const updateSessionState = (nextSession) => {
      if (!mounted) return;

      const normalizedSession = nextSession ?? null;
      const normalizedUser = normalizedSession?.user ?? null;

      setSession(normalizedSession);
      setUser(normalizedUser);

      try {
        if (normalizedUser?.id) {
          sessionStorage.setItem(
            "auth_uid",
            normalizedUser.id
          );
        } else {
          sessionStorage.removeItem("auth_uid");
        }
      } catch (error) {
        console.warn(
          "Unable to update session storage:",
          error
        );
      }
    };

    const restoreSession = async () => {
      try {
        const {
          data,
          error,
        } = await supabase.auth.getSession();

        if (!mounted) return;

        if (error) {
          console.warn(
            "Auth restore error:",
            error
          );
        }

        updateSessionState(data?.session ?? null);
      } catch (error) {
        console.error(
          "Auth session restoration failed:",
          error
        );

        updateSessionState(null);
      } finally {
        if (mounted) {
          setInitialized(true);
        }
      }
    };

    restoreSession();

    const {
      data: authListener,
    } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        updateSessionState(nextSession);
        setInitialized(true);
      }
    );

    return () => {
      mounted = false;

      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const value = useMemo(
    () => ({
      session,
      user,
      initialized,
      loading: !initialized,
    }),
    [session, user, initialized]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error(
      "useAuth must be used inside AuthProvider"
    );
  }

  return context;
};